import { useState, useEffect } from 'react'
import {
  CloseButton,
  Steps,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Input,
  NativeSelect,
  Badge,
  Spinner,
  SimpleGrid,
  useSteps,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiCheck, FiUser, FiSearch } from 'react-icons/fi'
import { useContactSearch } from '../../hooks/useContactSearch'
import { useContactDetail } from '../../hooks/useContactDetail'
import { useWorksiteSuggest, useWorksiteSearch } from '../../hooks/useBusiness'
import { useUnionSearch } from '../../hooks/useUnion'
import { useCreateEmployment } from '../../hooks/useEmployment'
import type { ContactSearchRequest, ContactSearchResult } from '../../types/contact'
import { ResponsiveModal } from '../shared'
import { ContactCreateForm } from '../contacts/ContactCreateForm'

const steps = [
  { title: 'Find Contact' },
  { title: 'Select Worksite' },
  { title: 'Employment Details' },
]

interface AddMemberWizardProps {
  isOpen: boolean
  onClose: () => void
  /** When set (e.g. opened from a union's own page), pre-selects this union on step 3. */
  unionId?: string
}

export function AddMemberWizard({ isOpen, onClose, unionId }: AddMemberWizardProps) {
  const stepsApi = useSteps({
    defaultStep: 0,
    count: steps.length
  })

  // Step 1: Contact search/create
  const [searchParams, setSearchParams] = useState<ContactSearchRequest>({ pageSize: 10 })
  const [nameInput, setNameInput] = useState({ firstName: '', lastName: '' })
  const [hasSearched, setHasSearched] = useState(false)
  const { data: contactResults, isLoading: searchLoading } = useContactSearch(searchParams, hasSearched)
  const [selectedContact, setSelectedContact] = useState<{ id: string; name: string } | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  // The shared ContactCreateForm's onDone contract yields only a contact id (Task 7-15 dependents
  // rely on that shape). Once it fires, fetch the contact's detail here just to get a display name
  // for the confirmation box / employment summary below.
  const [pendingContactId, setPendingContactId] = useState<string | null>(null)
  const { data: pendingContactDetail } = useContactDetail(pendingContactId ?? undefined)

  // Step 2: Worksite selection
  const [worksiteInput, setWorksiteInput] = useState('')
  const { data: worksiteSuggestions } = useWorksiteSuggest(worksiteInput)
  const { data: worksiteSearchData } = useWorksiteSearch(
    { search: worksiteInput || undefined, pageSize: 10 },
    { enabled: worksiteInput.length >= 2 }
  )
  const [selectedWorksite, setSelectedWorksite] = useState<{ id: string; label: string } | null>(null)

  // Step 3: Employment details
  const [empFields, setEmpFields] = useState({ position: '', department: '', unionId: unionId ?? '' })
  const { data: unionData } = useUnionSearch({ pageSize: 100 })
  const createEmployment = useCreateEmployment(selectedContact?.id ?? '')

  // Keep the default in sync if the wizard is opened for a different union (or re-opened
  // via the same instance) -- e.g. from a union's own detail page.
  useEffect(() => {
    if (unionId) {
      setEmpFields(p => ({ ...p, unionId }))
    }
  }, [unionId])

  // Once a display name resolves for a contact created via the shared form, refine the
  // placeholder label. This is best-effort only -- it must never gate the step-2 advance
  // (see handleFormCreated below), so a failed/slow fetch just leaves the fallback label.
  useEffect(() => {
    if (pendingContactId && pendingContactDetail) {
      setSelectedContact(prev =>
        prev && prev.id === pendingContactId
          ? { ...prev, name: `${pendingContactDetail.firstName} ${pendingContactDetail.lastName}` }
          : prev
      )
      setPendingContactId(null)
    }
  }, [pendingContactId, pendingContactDetail])

  const handleContactSearch = () => {
    if (!nameInput.lastName.trim()) {
      toaster.create({ title: 'Last name is required to search', type: 'warning', duration: 3000 })
      return
    }
    setSearchParams({
      lastName: nameInput.lastName.trim(),
      firstName: nameInput.firstName.trim() || undefined,
      pageSize: 10,
    })
    setHasSearched(true)
  }

  const handleFormCreated = (contactId: string) => {
    // Advance immediately -- steps 2-3 only need the contact id, which we already have.
    // The display name is best-effort (see the useContactDetail effect above); a failed
    // or slow name fetch must never strand the wizard on the create form.
    setSelectedContact({ id: contactId, name: 'New contact' })
    setPendingContactId(contactId)
    setShowCreateForm(false)
    stepsApi.setStep(1)
  }

  const handleFinish = async () => {
    if (!selectedContact || !selectedWorksite) return
    try {
      await createEmployment.mutateAsync({
        worksiteId: selectedWorksite.id,
        position: empFields.position || undefined,
        department: empFields.department || undefined,
        isActive: true,
        isPrimary: true,
        unionId: empFields.unionId || null,
      })
      toaster.create({
        title: 'Member added',
        description: `${selectedContact.name} linked to ${selectedWorksite.label}`,
        type: 'success',
        duration: 5000,
      })
      onClose()
    } catch {
      toaster.create({ title: 'Failed to create employment', type: 'error', duration: 3000 })
    }
  }

  // Combine suggest + search results, prefer suggest order
  const worksiteResults = worksiteSuggestions && worksiteSuggestions.length > 0
    ? worksiteSuggestions
    : worksiteSearchData?.items?.map(w => ({ ...w, similarity: 1, businessName: w.businessName })) ?? []

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="xl">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Add Union Member</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <Steps.RootProvider size="sm" mb={6} colorPalette="brand" value={stepsApi}>
              {steps.map((step, index) => (
                <Steps.Item key={index} index={index}>
                  <Steps.Indicator>
                    <Steps.Status
                      complete={<FiCheck />}
                      incomplete={<Box boxSize={2} bg="gray.300" borderRadius="full" />}
                      current={<Box boxSize={2} bg="accent" borderRadius="full" />}
                    />
                  </Steps.Indicator>
                  <Box flexShrink={0}>
                    <Steps.Title>{step.title}</Steps.Title>
                  </Box>
                  <Steps.Separator />
                </Steps.Item>
              ))}
            </Steps.RootProvider>

            {/* Step 1: Find or Create Contact */}
            {stepsApi.value === 0 && (
              <VStack gap={4} align="stretch">
                {!selectedContact && !showCreateForm && (
                  <>
                    <Text fontSize="sm" color="gray.600">
                      Search for an existing contact first. If they don't exist, you can create one.
                    </Text>
                    <SimpleGrid columns={2} gap={3}>
                      <Field.Root>
                        <Field.Label fontSize="sm">First Name</Field.Label>
                        <Input
                          size="sm"
                          value={nameInput.firstName}
                          onChange={e => setNameInput(p => ({ ...p, firstName: e.target.value }))}
                        />
                      </Field.Root>
                      <Field.Root required>
                        <Field.Label fontSize="sm">Last Name</Field.Label>
                        <Input
                          size="sm"
                          value={nameInput.lastName}
                          onChange={e => setNameInput(p => ({ ...p, lastName: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleContactSearch() }}
                        />
                      </Field.Root>
                    </SimpleGrid>
                    <Button size="sm" colorPalette="brand" onClick={handleContactSearch}><FiSearch />Search Contacts
                                        </Button>

                    {searchLoading && <Spinner size="sm" color="accent" />}

                    {hasSearched && !searchLoading && contactResults && (
                      <>
                        {contactResults.items.length === 0 ? (
                          <Box bg="orange.50" p={3} borderRadius="md">
                            <Text fontSize="sm" color="orange.700">
                              No contacts found matching that name.
                            </Text>
                            <Button
                              size="sm"
                              mt={2}
                              colorPalette="brand"
                              onClick={() => setShowCreateForm(true)}><FiUser />Create New Contact
                                                        </Button>
                          </Box>
                        ) : (
                          <VStack gap={1} align="stretch">
                            <Text fontSize="xs" color="gray.500" fontWeight="medium">
                              {contactResults.totalCount} result{contactResults.totalCount !== 1 ? 's' : ''} found
                            </Text>
                            {contactResults.items.map((c: ContactSearchResult) => (
                              <HStack
                                key={c.id}
                                p={2}
                                bg="gray.50"
                                borderRadius="md"
                                cursor="pointer"
                                _hover={{ bg: 'accent.soft' }}
                                onClick={() => {
                                  setSelectedContact({ id: c.id, name: c.fullName })
                                  stepsApi.setStep(1)
                                }}
                              >
                                <Box flex={1}>
                                  <Text fontSize="sm" fontWeight="medium">{c.fullName}</Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {[c.streetAddress, c.city, c.state].filter(Boolean).join(', ')}
                                    {c.phone ? ` | ${c.phone}` : ''}
                                  </Text>
                                </Box>
                                {c.employerName && <Badge fontSize="2xs" colorPalette="purple">{c.employerName}</Badge>}
                              </HStack>
                            ))}
                            <Button
                              size="xs"
                              variant="ghost"
                              mt={1}
                              onClick={() => setShowCreateForm(true)}
                              color="gray.500"
                            >
                              None of these — create new contact
                            </Button>
                          </VStack>
                        )}
                      </>
                    )}
                  </>
                )}

                {showCreateForm && !selectedContact && (
                  <VStack gap={3} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">Create New Contact</Text>
                      <Button size="xs" variant="ghost" onClick={() => setShowCreateForm(false)}>Back to Search</Button>
                    </HStack>
                    <ContactCreateForm context={{ kind: 'union', unionId }} onDone={handleFormCreated} />
                  </VStack>
                )}

                {selectedContact && (
                  <Box bg="green.50" p={3} borderRadius="md">
                    <HStack>
                      <FiCheck color="green" />
                      <Text fontSize="sm" fontWeight="medium">{selectedContact.name}</Text>
                      <Button size="xs" variant="ghost" onClick={() => { setSelectedContact(null); stepsApi.setStep(0) }}>Change</Button>
                    </HStack>
                  </Box>
                )}
              </VStack>
            )}

            {/* Step 2: Select Worksite */}
            {stepsApi.value === 1 && (
              <VStack gap={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  Search for the worksite where {selectedContact?.name} is employed.
                </Text>
                <Field.Root>
                  <Field.Label fontSize="sm">Search Worksites</Field.Label>
                  <Input
                    size="sm"
                    placeholder="Type worksite name..."
                    value={worksiteInput}
                    onChange={e => { setWorksiteInput(e.target.value); setSelectedWorksite(null) }}
                  />
                </Field.Root>

                {worksiteInput.length >= 2 && worksiteResults.length > 0 && !selectedWorksite && (
                  <VStack gap={1} align="stretch" maxH="200px" overflowY="auto">
                    {worksiteResults.map((w) => (
                      <HStack
                        key={w.id}
                        p={2}
                        bg="gray.50"
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: 'accent.soft' }}
                        onClick={() => {
                          setSelectedWorksite({ id: w.id, label: w.label })
                          setWorksiteInput(w.label)
                        }}
                      >
                        <Box flex={1}>
                          <Text fontSize="sm" fontWeight="medium">{w.label}</Text>
                          <HStack gap={2}>
                            {w.type && <Badge colorPalette="purple" fontSize="2xs">{w.type}</Badge>}
                            {w.city && <Text fontSize="xs" color="gray.500">{w.city}{w.state ? `, ${w.state}` : ''}</Text>}
                            {w.businessName && <Text fontSize="xs" color="gray.400">{w.businessName}</Text>}
                          </HStack>
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                )}

                {selectedWorksite && (
                  <Box bg="green.50" p={3} borderRadius="md">
                    <HStack>
                      <FiCheck color="green" />
                      <Text fontSize="sm" fontWeight="medium">{selectedWorksite.label}</Text>
                    </HStack>
                  </Box>
                )}
              </VStack>
            )}

            {/* Step 3: Employment Details */}
            {stepsApi.value === 2 && (
              <VStack gap={4} align="stretch">
                <Box bg="info.subtle" p={3} borderRadius="md">
                  <Text fontSize="sm">
                    <strong>{selectedContact?.name}</strong> at <strong>{selectedWorksite?.label}</strong>
                  </Text>
                </Box>
                <Field.Root>
                  <Field.Label fontSize="sm">Position</Field.Label>
                  <Input size="sm" value={empFields.position} onChange={e => setEmpFields(p => ({ ...p, position: e.target.value }))} placeholder="e.g., Deli Clerk" />
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Department</Field.Label>
                  <Input size="sm" value={empFields.department} onChange={e => setEmpFields(p => ({ ...p, department: e.target.value }))} placeholder="e.g., Produce" />
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Union</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      placeholder="Select union"
                      value={empFields.unionId}
                      onChange={e => setEmpFields(p => ({ ...p, unionId: e.target.value }))}>
                      {unionData?.items.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.fullName || u.name} {u.localNumber ? `(Local ${u.localNumber})` : ''}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </VStack>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            {stepsApi.value > 0 && (
              <Button variant="outline" mr={3} onClick={() => stepsApi.setStep(stepsApi.value - 1)}>
                Back
              </Button>
            )}
            {stepsApi.value === 1 && selectedWorksite && (
              <Button colorPalette="brand" onClick={() => stepsApi.setStep(2)}>
                Next
              </Button>
            )}
            {stepsApi.value === 2 && (
              <Button
                colorPalette="brand"
                onClick={handleFinish}
                loading={createEmployment.isPending}
              >
                Add Member
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
